'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { hasGraduatedFromIntro, type GameType } from '@puzzlio/core';
import { LocalStorageKeys } from '@/app/lib/constants';
import { useUserLoading } from '@/app/providers/UserProvider';
import { GAME_CONFIG, INTRO_GAME_TYPES } from '@/app/games/config';
import { usePlaysVersion, useScopeReady } from '@/app/lib/play-sync/client/PlayScope';
import { completedBoards } from '@/app/lib/play-sync/client/store';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Per-game flag: true while the player still sees the guided tutorial (hasn't graduated yet). */
export type IntroModeMap = Record<GameType, boolean>;

/** What local storage knows about onboarding for the current identity. */
type CachedProgress = {
  /** Per-game graduation flags. A missing game means "not graduated yet". */
  intro: Partial<IntroModeMap>;
};

interface ProgressContextValue {
  inIntroMode: IntroModeMap;
  /** False until this browser's graduation flags have been read; till then every game reads as in intro mode. */
  introModeLoaded: boolean;
  /** Graduate a game out of intro mode (the player finished or skipped the tutorial). */
  exitIntroMode: (gameType: GameType) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

// Only games with an intro sequence are tracked for intro mode. A playable game with no intro
// sequence is simply never in intro mode, so its links go to the daily puzzle. (Every playable
// game has one today; the guard matters when a new game is registered ahead of its tutorial.)
const GAME_TYPES = INTRO_GAME_TYPES;

// Every game starts in intro mode until local storage tells us the player has graduated.
const ALL_IN_INTRO = Object.fromEntries(GAME_TYPES.map((game) => [game, true])) as IntroModeMap;

// Graduation flags are stored under the game-type string, so renaming a game orphans the flags already
// written to returning players' browsers and drops them back into the tutorial. Old name -> current name,
// so a rename heals on the next page load with no server round trip. Entries are permanent: a browser
// that hasn't been opened since the rename still holds the old key.
const LEGACY_GAME_TYPE_ALIASES: Record<string, GameType> = {
  'lazy-river': 'loopy-river',
};

// Onboarding state is only consumed on the home page and the game pages, so we only look it up there.
// ProgressProvider lives in the root layout, so without this guard the lookup would run on every route
// — in particular the auth flow, where it adds DB load (and could stall the post-login redirect) for no
// benefit.
function isIntroSurface(pathname: string | null): boolean {
  return pathname === '/' || (pathname?.startsWith('/games') ?? false);
}

// ─── Local-storage helpers ────────────────────────────────────────────────────

// Namespaced by user identity so onboarding state doesn't bleed across accounts on a shared browser.
// Falls back to 'anon' when the identity isn't known yet.
function cacheKeyFor(identity: string | undefined): string {
  return `${LocalStorageKeys.INTRO_MODE_CACHE}:${identity ?? 'anon'}`;
}

// Reads the cached per-game intro-mode flags. A missing game key means "not graduated yet". Only
// `false` (graduated) is ever recorded, so a game is in intro mode unless the cache explicitly clears it.
function readIntroCache(identity: string | undefined): Partial<IntroModeMap> {
  try {
    const raw = localStorage.getItem(cacheKeyFor(identity));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    const out: Partial<IntroModeMap> = {};
    for (const game of GAME_TYPES) {
      if (typeof parsed[game] === 'boolean') out[game] = parsed[game] as boolean;
    }
    // A flag under the game's current name always wins; the old name only fills a gap it left behind.
    for (const [legacy, current] of Object.entries(LEGACY_GAME_TYPE_ALIASES)) {
      if (out[current] === undefined && typeof parsed[legacy] === 'boolean') out[current] = parsed[legacy] as boolean;
    }
    return out;
  } catch {
    return {};
  }
}

function readCache(identity: string | undefined): CachedProgress {
  return { intro: readIntroCache(identity) };
}

function writeCache(identity: string | undefined, games: GameType[], inIntroMode: boolean): void {
  try {
    const key = cacheKeyFor(identity);
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    for (const game of games) parsed[game] = inIntroMode;
    localStorage.setItem(key, JSON.stringify(parsed));
  } catch {
    // localStorage may be unavailable in some environments; fail silently.
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ProgressContext = createContext<ProgressContextValue>({
  inIntroMode: ALL_IN_INTRO,
  introModeLoaded: false,
  exitIntroMode: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ProgressProvider({ children }: { children: React.ReactNode }) {
  const [user, isUserLoading] = useUserLoading();
  // Stable per-account identity used to key the cache. Changes when the player logs in / out.
  const identity = user?.auth?.id ?? user?.user_str;

  // Only look up onboarding state on the surfaces that consume it (see isIntroSurface).
  const onIntroSurface = isIntroSurface(usePathname());

  // Onboarding state read from local storage once the identity is known. `null` means "not yet read";
  // until then every game defaults to intro mode.
  const [cached, setCached] = useState<CachedProgress | null>(null);

  useEffect(() => {
    if (isUserLoading) return;
    // Syncing external localStorage state into React when the identity resolves/changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCached(readCache(identity));
  }, [identity, isUserLoading]);

  // The games the player's own history shows they already know: built on other devices, or in this browser
  // before its onboarding state was cleared. Read from the synced copy of their plays, so it costs no
  // request, and only on the surfaces that use the answer.
  const { scope } = useScopeReady();
  const playsVersion = usePlaysVersion();
  const knownFromHistory = useMemo<GameType[]>(() => {
    if (!scope || !onIntroSurface) return [];
    const completed = completedBoards(scope);
    return GAME_TYPES.filter((game) =>
      hasGraduatedFromIntro(completed, GAME_CONFIG[game].gameId, GAME_CONFIG[game].tutorialSlug)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, onIntroSurface, playsVersion]);

  const inIntroMode = useMemo<IntroModeMap>(() => {
    const out = { ...ALL_IN_INTRO };
    for (const game of GAME_TYPES) {
      if (cached?.intro[game] === false) out[game] = false;
    }
    for (const game of knownFromHistory) out[game] = false;
    return out;
  }, [cached, knownFromHistory]);

  // Graduate a game: record it to local storage and flip the in-memory flag so consumers re-render.
  const exitIntroMode = useCallback(
    (gameType: GameType) => {
      writeCache(identity, [gameType], false);
      setCached((prev) => ({ intro: { ...(prev?.intro ?? {}), [gameType]: false } }));
    },
    [identity]
  );

  const introModeLoaded = cached !== null;
  const value = useMemo(
    () => ({ inIntroMode, introModeLoaded, exitIntroMode }),
    [inIntroMode, introModeLoaded, exitIntroMode]
  );

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useInIntroMode(): IntroModeMap {
  return useContext(ProgressContext).inIntroMode;
}

export function useIsInIntroMode(gameType: GameType): boolean {
  return useContext(ProgressContext).inIntroMode[gameType];
}

export function useIntroModeLoaded(): boolean {
  return useContext(ProgressContext).introModeLoaded;
}

export function useExitIntroMode(): (gameType: GameType) => void {
  return useContext(ProgressContext).exitIntroMode;
}
